import React, {useCallback, useMemo} from 'react';
import {PlanGetSchema} from '../../../../api/DBSchema/Plan';
import SMCLabel from '../../../../components/Simple/SMCLabel';
import Indentation from '../../../../components/Simple/Indentation';
import {SMCRadioGroup, SMCRadio} from '../../../../components/Simple/SMCRadio';
import {SMCSelect, SMCOption} from '../../../../components/Simple/SMCSelect';
import {useTranslation} from 'react-i18next';
import {FirmwareGetListSchema} from '../../../../api/DBSchema/Firmware';
import EmptyRow from '../../../../components/Simple/EmptyRow';
import EditorLayout from '../../../../components/Layouts/EditorLayout';
import {SMCDisabledProvider} from '../../../../components/Simple/SMCDisabledContext';
import SMCButton from '../../../../components/Simple/SMCButton';
import GradualUpgradeModal from '../../../PopupPages/GradualUpgradeModal';
import {useBoolean} from '../../../../utils/hooks';

const pageSchema = PlanGetSchema.pages.firmware;
const pageId = pageSchema.pageId;

function PlanFirmware({
    planObject,
    origPlanObject,
    availableFirmwares,
    setPlanData
}) {
    const { t } = useTranslation();
    
    const pageData = planObject[pageId];

    const mode = pageData[pageSchema.mode];
    const firmware = pageData[pageSchema.firmware];
    const firmware1200 = pageData[pageSchema.firmware1200];
    const firmwareS1S2 = pageData[pageSchema.firmwareS1S2];
    const firmwareV0V1 = pageData[pageSchema.firmwareV0V1];
    const firmwareV2V3 = pageData[pageSchema.firmwareV2V3];
    const time = pageData[pageSchema.time];
    const weekDay = pageData[pageSchema.weekDay];
    const monthDay = pageData[pageSchema.monthDay];
    const frequency = pageData[pageSchema.frequency];
    // Gradual vars
    const gradualFirmware = pageData[pageSchema.gradualFirmware];
    const gradualFirmware1200 = pageData[pageSchema.gradualFirmware1200];
    const gradualFirmwareS1S2 = pageData[pageSchema.gradualFirmwareS1S2];
    const gradualFirmwareV0V1 = pageData[pageSchema.gradualFirmwareV0V1];
    const gradualFirmwareV2V3 = pageData[pageSchema.gradualFirmwareV2V3];
    const gradualUpgradeStartDate = pageData[pageSchema.gradualUpgradeStartDate];
    const gradualUpgradeRunTime = pageData[pageSchema.gradualUpgradeRunTime];
    
    const modeValues = pageSchema.modeValues;
    const frequencyValues = pageSchema.frequencyValues;

    const [isGradualUpgradeModalOpen, openGradualUpgradeModal, closeGradualUpgradeModal] = useBoolean(false);

    const setPageValue = useCallback((param, newValue) => {
        setPlanData([pageId, param], newValue);
    }, [setPlanData]);

    const isFirmwareChosen = useMemo(() => (
        firmware !== 'no firmware configured' ||
        firmware1200 !== 'no firmware configured' ||
        firmwareS1S2 !== 'no firmware configured' ||
        firmwareV0V1 !== 'no firmware configured' ||
        firmwareV2V3 !== 'no firmware configured'
    ), [firmware, firmware1200, firmwareS1S2, firmwareV0V1, firmwareV2V3]);

    const isGradualFirmwareChosen = useMemo(() => (
        gradualFirmware !== 'no firmware configured' ||
        gradualFirmware1200 !== 'no firmware configured' ||
        gradualFirmwareS1S2 !== 'no firmware configured' ||
        gradualFirmwareV0V1 !== 'no firmware configured' ||
        gradualFirmwareV2V3 !== 'no firmware configured'
    ), [gradualFirmware, gradualFirmware1200, gradualFirmwareS1S2, gradualFirmwareV0V1, gradualFirmwareV2V3]);

    // Options of 00:00-01:00 until 23:00-00:00
    const timeOptions = useMemo(() => (
        [...Array(24)].map((e,i) => (
            <SMCOption key={i} value={i}>{i<10?'0':''}{i}:00-{((i+1)%24)<10?'0':''}{(i+1)%24}:00</SMCOption>
        ))
    ), []);

    const dayOfWeekOptions = useMemo(() => (
        <>
        <SMCOption value={1}>{t('SMP.SUNDAY')}</SMCOption>
        <SMCOption value={2}>{t('SMP.MONDAY')}</SMCOption>
        <SMCOption value={3}>{t('SMP.TUESDAY')}</SMCOption>
        <SMCOption value={4}>{t('SMP.WEDNESDAY')}</SMCOption>
        <SMCOption value={5}>{t('SMP.THURSDAY')}</SMCOption>
        <SMCOption value={6}>{t('SMP.FRIDAY')}</SMCOption>
        <SMCOption value={7}>{t('SMP.SATURDAY')}</SMCOption>
        </>
    ), [t]);

    const dayOfMonthOptions = useMemo(() => (
        [...Array(31)].map((e,i) => (
            <SMCOption key={i+1} value={i+1}>{i+1}</SMCOption>
        ))
    ), []);

    const firmware600Options = useMemo(() => (
        availableFirmwares?.firmware600List?.map(firm => (
            <SMCOption value={firm[FirmwareGetListSchema.name].toLowerCase()} key={firm[FirmwareGetListSchema.name]}>{firm[FirmwareGetListSchema.name]}</SMCOption>
        ))
    ), [availableFirmwares]);

    const firmware1200Options = useMemo(() => (
        availableFirmwares?.firmware1200List?.map(firm => (
            <SMCOption value={firm[FirmwareGetListSchema.name].toLowerCase()} key={firm[FirmwareGetListSchema.name]}>{firm[FirmwareGetListSchema.name]}</SMCOption>
        ))
    ), [availableFirmwares]);

    const firmwareS1S2Options = useMemo(() => (
        availableFirmwares?.firmwareS1S2List?.map(firm => (
            <SMCOption value={firm[FirmwareGetListSchema.name].toLowerCase()} key={firm[FirmwareGetListSchema.name]}>{firm[FirmwareGetListSchema.name]}</SMCOption>
        ))
    ), [availableFirmwares]);

    const firmwareV0V1Options = useMemo(() => (
        availableFirmwares?.firmwareV0V1List?.map(firm => (
            <SMCOption value={firm[FirmwareGetListSchema.name].toLowerCase()} key={firm[FirmwareGetListSchema.name]}>{firm[FirmwareGetListSchema.name]}</SMCOption>
        ))
    ), [availableFirmwares]);

    const firmwareV2V3Options = useMemo(() => (
        availableFirmwares?.firmwareV2V3List?.map(firm => (
            <SMCOption value={firm[FirmwareGetListSchema.name].toLowerCase()} key={firm[FirmwareGetListSchema.name]}>{firm[FirmwareGetListSchema.name]}</SMCOption>
        ))
    ), [availableFirmwares]);

    return (
        <EditorLayout labelWidth={240}>
            <SMCLabel bold>{t('SMP.PLAN.SERVICES.FIRMWARE.LINE1')}</SMCLabel>
            <Indentation>
                <SMCRadioGroup
                    value={mode}
                    onChange={e => {
                        const newMode = e.target.value;
                        setPageValue(pageSchema.mode, newMode);
                        if (newMode === modeValues.gradual && (frequency === frequencyValues.weekly || frequency === frequencyValues.monthly)) {
                            setPageValue(pageSchema.frequency, frequencyValues.daily);
                        }
                    }}
                >
                    <SMCRadio value={modeValues.specific} label={t('SMP.PLAN.SERVICES.FIRMWARE.LINE2')} />
                    <Indentation>
                        {/* Disable firmware choice if mode isn't specific */}
                        <SMCDisabledProvider forceDisable={mode !== modeValues.specific}>
                            {/* 600/1100 Firmware */}
                            <SMCSelect width={350} value={firmware} onChange={e => setPageValue(pageSchema.firmware, e.target.value)} label={t('SMP.PLAN.SERVICES.FIRMWARE.600_1100_APPLIANCE')}>
                                <SMCOption value="no firmware configured">Do_Not_Upgrade</SMCOption>
                                { firmware600Options }
                            </SMCSelect>
                            {/* 1200R Firmware */}
                            <SMCSelect width={350} value={firmware1200} onChange={e => setPageValue(pageSchema.firmware1200, e.target.value)} label={t('SMP.PLAN.SERVICES.FIRMWARE.1200R_APPLIANCE')}>
                                <SMCOption value="no firmware configured">Do_Not_Upgrade</SMCOption>
                                { firmware1200Options }
                            </SMCSelect>
                            {/* S1S2 Firmware */}
                            <SMCSelect width={350} value={firmwareS1S2} onChange={e => setPageValue(pageSchema.firmwareS1S2, e.target.value)} label={t('SMP.PLAN.SERVICES.FIRMWARE.700_1400_APPLIANCE')}>
                                <SMCOption value="no firmware configured">Do_Not_Upgrade</SMCOption>
                                { firmwareS1S2Options }
                            </SMCSelect>
                            {/* V0V1 Firmware */}
                            <SMCSelect width={350} value={firmwareV0V1} onChange={e => setPageValue(pageSchema.firmwareV0V1, e.target.value)} label={t('SMP.PLAN.SERVICES.FIRMWARE.1500_1500R_APPLIANCE')}>
                                <SMCOption value="no firmware configured">Do_Not_Upgrade</SMCOption>
                                { firmwareV0V1Options }
                            </SMCSelect>
                            {/* V2V3 Firmware */}
                            <SMCSelect width={350} value={firmwareV2V3} onChange={e => setPageValue(pageSchema.firmwareV2V3, e.target.value)} label={t('SMP.PLAN.SERVICES.FIRMWARE.1600_1800_APPLIANCE')}>
                                <SMCOption value="no firmware configured">Do_Not_Upgrade</SMCOption>
                                { firmwareV2V3Options }
                            </SMCSelect>
                        </SMCDisabledProvider>
                    </Indentation>
                    <SMCRadio value={modeValues.latest} label={t('SMP.PLAN.SERVICES.FIRMWARE.CHECKPOINT_LATEST')} />
                    <SMCRadio value={modeValues.local} label={t('SMP.PLAN.SERVICES.FIRMWARE.LINE3')} />
                    <SMCRadio value={modeValues.gradual} label={t('SMP.PLAN.SERVICES.FIRMWARE.GRADUAL_UPGRADE')} />
                    <Indentation>
                        {/* Disable gradual firmware choice if mode isn't gradual */}
                        <SMCDisabledProvider forceDisable={mode !== modeValues.gradual}>
                            {/* 600/1100 Firmware */}
                            <SMCSelect disabled={!!gradualUpgradeStartDate || !!gradualUpgradeRunTime} width={350} value={gradualFirmware} onChange={e => setPageValue(pageSchema.gradualFirmware, e.target.value)} label={t('SMP.PLAN.SERVICES.FIRMWARE.600_1100_APPLIANCE')}>
                                <SMCOption value="no firmware configured">Do_Not_Upgrade</SMCOption>
                                { firmware600Options }
                            </SMCSelect>
                            {/* 1200R Firmware */}
                            <SMCSelect disabled={!!gradualUpgradeStartDate || !!gradualUpgradeRunTime} width={350} value={gradualFirmware1200} onChange={e => setPageValue(pageSchema.gradualFirmware1200, e.target.value)} label={t('SMP.PLAN.SERVICES.FIRMWARE.1200R_APPLIANCE')}>
                                <SMCOption value="no firmware configured">Do_Not_Upgrade</SMCOption>
                                { firmware1200Options }
                            </SMCSelect>
                            {/* S1S2 Firmware */}
                            <SMCSelect disabled={!!gradualUpgradeStartDate || !!gradualUpgradeRunTime} width={350} value={gradualFirmwareS1S2} onChange={e => setPageValue(pageSchema.gradualFirmwareS1S2, e.target.value)} label={t('SMP.PLAN.SERVICES.FIRMWARE.700_1400_APPLIANCE')}>
                                <SMCOption value="no firmware configured">Do_Not_Upgrade</SMCOption>
                                { firmwareS1S2Options }
                            </SMCSelect>
                            {/* V0V1 Firmware */}
                            <SMCSelect disabled={!!gradualUpgradeStartDate || !!gradualUpgradeRunTime} width={350} value={gradualFirmwareV0V1} onChange={e => setPageValue(pageSchema.gradualFirmwareV0V1, e.target.value)} label={t('SMP.PLAN.SERVICES.FIRMWARE.1500_1500R_APPLIANCE')}>
                                <SMCOption value="no firmware configured">Do_Not_Upgrade</SMCOption>
                                { firmwareV0V1Options }
                            </SMCSelect>
                            {/* V2V3 Firmware */}
                            <SMCSelect disabled={!!gradualUpgradeStartDate || !!gradualUpgradeRunTime} width={350} value={gradualFirmwareV2V3} onChange={e => setPageValue(pageSchema.gradualFirmwareV2V3, e.target.value)} label={t('SMP.PLAN.SERVICES.FIRMWARE.1600_1800_APPLIANCE')}>
                                <SMCOption value="no firmware configured">Do_Not_Upgrade</SMCOption>
                                { firmwareV2V3Options }
                            </SMCSelect>
                            <SMCButton onClick={openGradualUpgradeModal}>{t('SMP.PLAN.SERVICES.FIRMWARE.GRADUAL_UPGRADE_CONTROL')}</SMCButton>
                        </SMCDisabledProvider>
                    </Indentation>
                </SMCRadioGroup>
                {
                    mode === modeValues.gradual && (gradualUpgradeStartDate || gradualUpgradeRunTime) && (
                        <SMCLabel><b>{t('SMP.PLAN.SERVICES.FIRMWARE.NOTE')}: </b>{t('SMP.PLAN.SERVICES.FIRMWARE.LINE4')}</SMCLabel>
                    )
                }
            </Indentation>
            <EmptyRow />
            <SMCLabel bold>{t('SMP.PLAN.SERVICES.FIRMWARE.LINE5')}</SMCLabel>
            <Indentation>
                {/* Disable time choice if mode is local or ((specific or gradual)) with no firmware chosen) */}
                <SMCDisabledProvider forceDisable={mode === modeValues.local || (mode === modeValues.specific && !isFirmwareChosen) || (mode === modeValues.gradual && !isGradualFirmwareChosen)}>
                    <SMCRadioGroup value={frequency} onChange={e => setPageValue(pageSchema.frequency, e.target.value)}>
                        <SMCRadio value={frequencyValues.immediately} label={t('SMP.PLAN.SERVICES.FIRMWARE.UPGRADE_IMMEDIATELY')} />
                        <SMCRadio value={frequencyValues.daily} label={t('SMP.PLAN.SERVICES.FIRMWARE.DAILY')} />
                        {
                            frequency === frequencyValues.daily && (
                                <Indentation>
                                    <SMCSelect width={130} value={time} onChange={e => setPageValue(pageSchema.time, parseInt(e.target.value))} label={t('SMP.PLAN.SERVICES.FIRMWARE.TIME_OF_DAY')}>
                                        {timeOptions}
                                    </SMCSelect>
                                </Indentation>
                            )
                        }
                        {
                            // Show weekly and monthly only when mode is not gradual
                            mode !== modeValues.gradual && (
                                <>
                                <SMCRadio value={frequencyValues.weekly} label={t('SMP.PLAN.SERVICES.FIRMWARE.WEEKLY')} />
                                {
                                    frequency === frequencyValues.weekly && (
                                        <Indentation>
                                            <SMCSelect width={130} value={weekDay} onChange={e => setPageValue(pageSchema.weekDay, parseInt(e.target.value))} label={t('SMP.PLAN.SERVICES.FIRMWARE.DAY_OF_WEEK')}>
                                                {dayOfWeekOptions}
                                            </SMCSelect>
                                            <SMCSelect width={130} value={time} onChange={e => setPageValue(pageSchema.time, parseInt(e.target.value))} label={t('SMP.PLAN.SERVICES.FIRMWARE.TIME_OF_DAY')}>
                                                {timeOptions}
                                            </SMCSelect>
                                        </Indentation>
                                    )
                                }
                                <SMCRadio value={frequencyValues.monthly} label={t('SMP.PLAN.SERVICES.FIRMWARE.MONTHLY')} />
                                {
                                    frequency === frequencyValues.monthly && (
                                        <Indentation>
                                            <SMCSelect width={130} value={monthDay} onChange={e => setPageValue(pageSchema.monthDay, parseInt(e.target.value))} label={t('SMP.PLAN.SERVICES.FIRMWARE.DAY_OF_MONTH')}>
                                                {dayOfMonthOptions}
                                            </SMCSelect>
                                            <SMCSelect width={130} value={time} onChange={e => setPageValue(pageSchema.time, parseInt(e.target.value))} label={t('SMP.PLAN.SERVICES.FIRMWARE.TIME_OF_DAY')}>
                                                {timeOptions}
                                            </SMCSelect>
                                        </Indentation>
                                    )
                                }
                                </>
                            )
                        }
                    </SMCRadioGroup>
                </SMCDisabledProvider>
            </Indentation>
            {/* Popup windows */}
            <GradualUpgradeModal
                isOpen={isGradualUpgradeModalOpen}
                onRequestClose={closeGradualUpgradeModal}
                planObject={planObject}
                originalPlanObject={origPlanObject}
                onSuccess={newFirmwarePageData => {
                    setPageValue(pageSchema.gradualUpgradePercentage, newFirmwarePageData[pageSchema.gradualUpgradePercentage]);
                    setPageValue(pageSchema.gradualUpgradeDelay, newFirmwarePageData[pageSchema.gradualUpgradeDelay]);
                    setPageValue(pageSchema.gradualUpgradeRunTime, newFirmwarePageData[pageSchema.gradualUpgradeRunTime]);
                    setPageValue(pageSchema.gradualUpgradeStartDate, newFirmwarePageData[pageSchema.gradualUpgradeStartDate]);
                }}
            />
        </EditorLayout>
    );
}

export default PlanFirmware;